import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import TopBar from "../components/TopBar";
import CefrBadge from "../components/CefrBadge";
import { CEFR_COLORS, CEFR_ORDER, CEFR_LABELS, formatDateTime, formatDuration } from "../lib/cefr";
import {
  ArrowRight, Trophy, Timer, Books, ChartLineUp, Sparkle, ArrowUpRight, ClockCountdown,
} from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";

const SECTION_LABELS = {
  grammar: "Grammar",
  vocabulary: "Vocab.",
  reading: "Reading",
  listening: "Listening",
  writing: "Writing",
  speaking: "Speaking",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [attempts, setAttempts] = useState(null);
  const [allowance, setAllowance] = useState(null);

  useEffect(() => {
    api.get("/test/attempts").then((r) => setAttempts(r.data)).catch(() => setAttempts([]));
    api.get("/test/allowance").then((r) => setAllowance(r.data)).catch(() => {});
  }, []);

  const latest = attempts?.[0];
  const best = attempts?.reduce((a, c) => (!a || c.total_score > a.total_score ? c : a), null);
  const chartData = (attempts || []).slice().reverse().map((a, i) => ({
    idx: i + 1, score: a.total_score, level: a.cefr_level, date: formatDateTime(a.created_at),
  }));

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 px-2">
          <div>
            <div className="overline">Dashboard</div>
            <h1 className="text-4xl md:text-5xl mt-3 text-foreground font-bold tracking-tight" data-testid="dashboard-greeting">
              Welcome back,{" "}
              <span className="font-display italic text-indigo font-medium">{user?.name || "learner"}</span>.
            </h1>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2">
            {allowance?.left === 0 ? (
              <span data-testid="dashboard-start-test-btn" aria-disabled="true" className="inline-flex items-center gap-2 px-6 py-3 font-medium border border-foreground/30 text-muted-foreground cursor-not-allowed">
                No attempts left
              </span>
            ) : (
              <Link
                to="/test"
                data-testid="dashboard-start-test-btn"
                className="neu-btn-primary group inline-flex items-center gap-2 px-6 py-3 font-medium"
              >
                {attempts && attempts.length > 0 ? "Take another test" : "Start your first test"}
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
            {allowance && !allowance.unlimited && (
              <span className="text-xs text-muted-foreground font-mono-fig" data-testid="dashboard-attempts-left">
                {allowance.left} of {allowance.limit} test attempts left
              </span>
            )}
          </div>
        </div>

        {attempts === null ? (
          <div className="mt-16 text-center font-mono-fig text-sm text-muted-foreground">Loading…</div>
        ) : attempts.length === 0 ? (
          <EmptyState />
        ) : (
          <GridContent attempts={attempts} latest={latest} best={best} chartData={chartData} allowance={allowance} />
        )}
      </div>
    </div>
  );
}

function GridContent({ attempts, latest, best, chartData, allowance }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {/* Latest */}
      <div className="md:col-span-5 neu rounded-3xl p-8 flex flex-col justify-between min-h-[280px]" data-testid="dashboard-latest-card">
        <div className="flex items-center gap-2 overline">
          <Timer size={12} weight="duotone" /> Latest attempt
        </div>
        <div>
          <div className="flex items-end gap-3">
            <div className="font-mono-fig text-7xl md:text-8xl text-foreground leading-none">
              {Math.round(latest.total_score)}
            </div>
            <div className="pb-2 text-sm text-muted-foreground">/ {latest.total_max}</div>
          </div>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <CefrBadge level={latest.cefr_level} size="md" testId="dashboard-latest-badge" />
            <span className="text-sm text-foreground/80">{CEFR_LABELS[latest.cefr_level]}</span>
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div className="text-xs text-muted-foreground">
            {formatDateTime(latest.created_at)}
            {latest.duration_seconds != null && <> · {formatDuration(latest.duration_seconds)}</>}
          </div>
          <Link
            to={`/results/${latest.attempt_id}`}
            data-testid="dashboard-view-latest-link"
            className="neu-btn text-xs inline-flex items-center gap-1.5 px-4 py-2 hover:text-indigo"
          >
            Details <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>
      </div>

      {/* Personal best */}
      <div className="md:col-span-3 neu rounded-3xl p-6 flex flex-col justify-between min-h-[280px]" data-testid="dashboard-best-card">
        <div className="flex items-center gap-2 overline">
          <Trophy size={12} weight="duotone" /> Personal best
        </div>
        <div>
          <div className="font-mono-fig text-6xl text-foreground leading-none">
            {Math.round(best.total_score)}
          </div>
          <div className="mt-4">
            <CefrBadge level={best.cefr_level} size="sm" />
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Across {attempts.length} attempt{attempts.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* CEFR position */}
      <div className="md:col-span-4 neu rounded-3xl p-6 min-h-[280px] flex flex-col">
        <div className="overline flex items-center gap-2">
          <Books size={12} weight="duotone" /> Your position
        </div>
        <div className="mt-5 space-y-2 flex-1 flex flex-col justify-center">
          {CEFR_ORDER.map((lvl) => {
            const isCurrent = latest.cefr_level === lvl;
            return (
              <div
                key={lvl}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${isCurrent ? "bg-indigo/10" : ""}`}
              >
                <div
                  className={`w-8 h-7 flex items-center justify-center font-mono-fig text-[10px] font-bold ${isCurrent ? "text-ink" : "text-muted-foreground border border-foreground/30"}`}
                  style={isCurrent ? { background: CEFR_COLORS[lvl] } : undefined}
                >
                  {lvl}
                </div>
                <div className={`text-sm ${isCurrent ? "text-foreground font-medium" : "text-foreground/65"}`}>
                  {CEFR_LABELS[lvl]}
                </div>
                {isCurrent && (
                  <div className="ml-auto overline text-indigo" data-testid="dashboard-ladder-current">
                    ← you
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Skill breakdown */}
      <div className="md:col-span-5 neu rounded-3xl p-6">
        <div className="overline flex items-center gap-2">
          <Sparkle size={12} weight="fill" className="text-indigo" /> Latest — by skill
        </div>
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-3">
          {Object.keys(SECTION_LABELS).map((s) => {
            const score = latest.section_scores?.[s] ?? 0;
            const max = latest.section_max?.[s] ?? 20;
            const pct = Math.round((score / max) * 100);
            return (
              <div key={s} className="rounded-2xl p-3 bg-foreground/[0.04]">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{SECTION_LABELS[s]}</div>
                <div className="font-mono-fig text-xl text-foreground mt-1">{Math.round(score)}</div>
                <div className="text-[10px] text-muted-foreground">of {max}</div>
                <div className="mt-2 h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                  <div className="h-full rounded-full bg-indigo" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity */}
      <div className="md:col-span-3 neu rounded-3xl p-6 flex flex-col justify-between">
        <div className="overline flex items-center gap-2">
          <ClockCountdown size={12} weight="duotone" /> Activity
        </div>
        <div>
          <div className="font-mono-fig text-6xl text-foreground leading-none">{attempts.length}</div>
          <div className="text-xs text-muted-foreground mt-2">total attempt{attempts.length === 1 ? "" : "s"}</div>
        </div>
        <div className="text-xs text-indigo font-mono-fig">
          {latest.cefr_level === best.cefr_level && attempts.length > 1
            ? "steady at " + latest.cefr_level
            : attempts.length === 1
              ? "first result recorded"
              : `${best.cefr_level} personal best`}
        </div>
      </div>

      {/* Retake CTA */}
      <div className="md:col-span-4 neu rounded-3xl p-6 flex flex-col justify-between">
        <div className="overline">Test again</div>
        <div>
          <div className="text-2xl text-foreground font-bold leading-tight">
            Can you climb <span className="font-display italic text-indigo font-medium">one level?</span>
          </div>
          <p className="text-xs text-foreground/70 mt-2" data-testid="dashboard-retake-note">
            {allowance && !allowance.unlimited
              ? allowance.left > 0
                ? `You have ${allowance.left} of ${allowance.limit} attempts left — your history stays.`
                : "You have used all your attempts. Contact the school if you need another one."
              : "Retake anytime — your history stays."}
          </p>
        </div>
        {allowance?.left !== 0 && (
          <Link
            to="/test"
            data-testid="dashboard-retake-cta"
            className="neu-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm self-start"
          >
            Start now <ArrowRight size={14} weight="bold" />
          </Link>
        )}
      </div>

      {/* Chart */}
      <div className="md:col-span-12 neu rounded-3xl p-8">
        <div className="flex items-center justify-between mb-4">
          <div className="overline flex items-center gap-2">
            <ChartLineUp size={12} weight="duotone" /> Score progression
          </div>
          <div className="text-xs text-muted-foreground font-mono-fig">
            {attempts.length} data point{attempts.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="h-[280px]" data-testid="dashboard-progress-chart" style={{ minHeight: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid stroke="hsl(var(--foreground) / 0.12)" vertical={false} />
              <XAxis
                dataKey="idx"
                stroke="hsl(var(--foreground))"
                tick={{ fontFamily: "JetBrains Mono", fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                label={{ value: "Attempt #", position: "insideBottom", offset: -5, fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                domain={[0, 100]}
                stroke="hsl(var(--foreground))"
                tick={{ fontFamily: "JetBrains Mono", fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              />
              <Tooltip
                contentStyle={{ background: "hsl(var(--foreground))", color: "hsl(var(--background))", border: "none", fontSize: 12, borderRadius: 0, fontFamily: "JetBrains Mono" }}
                formatter={(value, _n, item) => [`${value} · ${item.payload.level}`, "Score"]}
              />
              {[20, 35, 55, 75, 90].map((v) => (
                <ReferenceLine key={v} y={v} stroke="hsl(var(--foreground) / 0.25)" strokeDasharray="2 4" />
              ))}
              <Line
                type="linear"
                dataKey="score"
                stroke="#ff8000"
                strokeWidth={2}
                dot={{ r: 4, fill: "#ff8000", strokeWidth: 0 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Past attempts */}
      <div className="md:col-span-12 neu rounded-3xl p-8">
        <div className="overline mb-4">Past attempts</div>
        <div className="space-y-3" data-testid="dashboard-attempts-list">
          {attempts.map((a) => (
            <Link
              key={a.attempt_id}
              to={`/results/${a.attempt_id}`}
              data-testid={`dashboard-attempt-${a.attempt_id}`}
              className="rounded-2xl grid grid-cols-12 gap-4 items-center px-5 py-4 bg-foreground/[0.03] hover:bg-foreground/[0.07] transition-colors group"
            >
              <div className="col-span-2 font-mono-fig text-2xl text-foreground">
                {Math.round(a.total_score)}
                <span className="text-sm text-muted-foreground"> / {a.total_max}</span>
              </div>
              <div className="col-span-3">
                <CefrBadge level={a.cefr_level} size="sm" />
              </div>
              <div className="col-span-4 text-sm text-muted-foreground">
                {formatDateTime(a.created_at)}
              </div>
              <div className="col-span-2 text-sm text-muted-foreground font-mono-fig">
                {formatDuration(a.duration_seconds)}
              </div>
              <div className="col-span-1 text-right text-foreground/60 group-hover:text-indigo transition-colors">
                <ArrowRight size={18} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="dashboard-empty-state">
      <div className="md:col-span-8 neu rounded-3xl p-10 md:p-14">
        <div className="overline">No attempts yet</div>
        <h2 className="text-4xl md:text-5xl mt-4 text-foreground font-bold tracking-tight leading-tight">
          Ready for your{" "}
          <span className="font-display italic text-indigo font-medium">first test</span>?
        </h2>
        <p className="mt-4 text-muted-foreground max-w-md leading-relaxed">
          The comprehensive test takes about 80 minutes and covers grammar,
          vocabulary, reading, listening, writing and speaking. You'll need a
          microphone for the speaking section.
        </p>
        <Link
          to="/test"
          data-testid="dashboard-empty-cta"
          className="neu-btn-primary inline-flex items-center gap-2 px-7 py-3.5 mt-10 font-medium"
        >
          Start the test <ArrowRight size={16} weight="bold" />
        </Link>
      </div>

      <div className="md:col-span-4 neu rounded-3xl p-8 flex flex-col justify-between min-h-[280px]">
        <div>
          <div className="overline">What to expect</div>
          <ul className="mt-6 space-y-4 text-sm text-foreground/80">
            {[
              { n: "01", t: "6 sections, 87 questions + speaking" },
              { n: "02", t: "80 minutes on the clock" },
              { n: "03", t: "Instant CEFR result A1–C2" },
            ].map((x) => (
              <li key={x.n} className="flex gap-3">
                <span className="font-mono-fig text-indigo">{x.n}</span>
                {x.t}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-muted-foreground">No AI grading · deterministic scoring.</div>
      </div>
    </div>
  );
}
